import * as React from 'react';

import * as colors from '../colors';

function stackMiles(
    memberMiles: number[], miles, delta: number,
): {
    avg: number,
    count: number,
    player: boolean,
}[] {
    memberMiles = [...memberMiles];
    memberMiles.sort((a, b) => a - b);
    const groupedMiles: number[][] = [];
    let lastMiles = memberMiles[0];
    let milesStack = [memberMiles[0]];
    for (let i = 1, iMax = memberMiles.length; i < iMax; i++) {
        const currMiles = memberMiles[i];
        if (currMiles - lastMiles < delta) {
            milesStack.push(currMiles);
        } else {
            groupedMiles.push(milesStack);
            milesStack = [currMiles];
            lastMiles = currMiles;
        }
    }
    groupedMiles.push(milesStack);
    return groupedMiles.map(arr => ({
        avg: arr.reduce((a, b) => a + b, 0) / arr.length,
        count: arr.length,
        player: miles >= Math.min(...arr) && miles < Math.min(...arr) + delta,
    }));
}

function miles2pos(miles: number, maxMiles: number): number {
    return miles / maxMiles * 100.0 - 4;
}

function nice_num(num: number, round_or_ceil: boolean): number {
    // From http://stackoverflow.com/a/16363437
    let nice_fraction = 0.0;
    const exponent = Math.floor(Math.log(num) / Math.LN10);
    const fraction = num / Math.pow(10, exponent);

    if (round_or_ceil) {
        if (fraction < 1.5) {
            nice_fraction = 1;
        } else if (fraction < 3) {
            nice_fraction = 2;
        } else if (fraction < 7) {
            nice_fraction = 5;
        } else {
            nice_fraction = 10;
        }
    } else {
        if (fraction <= 1) {
            nice_fraction = 1;
        } else if (fraction <= 2) {
            nice_fraction = 2;
        } else if (fraction <= 5) {
            nice_fraction = 5;
        } else {
            nice_fraction = 10;
        }
    }

    return nice_fraction * Math.pow(10, exponent);
}

function calcTicks(miles: number): [number, number][] {
    const max_ticks = 11;
    const tick_range = nice_num(miles, false);
    const tick_spacing = nice_num(tick_range / (max_ticks - 1), true);
    const nice_max = Math.ceil(miles / tick_spacing) * tick_spacing;
    const num_ticks = Math.floor(nice_max / tick_spacing) + 1;
    const tick_distance = 100.0 / Math.floor(nice_max / tick_spacing);
    const tick_miles: [number, number][] = [];
    for (let i = 0, iMax = num_ticks; i < iMax; i++) {
        tick_miles.push([i * tick_distance, i * tick_spacing]);

    }
    return tick_miles;
}

function calcMinorTicks(ticks: [number, number][]): number[] {
    const minor_ticks: number[] = [];
    for (let i = 1, iMax = ticks.length; i < iMax; i++) {
        const dist = (ticks[i][0] - ticks[i - 1][0]) / 2;
        minor_ticks.push(ticks[i][0] - dist);
    }
    return minor_ticks;
}

function Banner({ loc }: { loc: number }): JSX.Element {
    return <g transform={`translate(${loc - 2.5}, 0.1)`}>
        <line x1='0' y1='0.5' x2='0' y2='7' stroke='#020202' strokeWidth='0.3' />
        <polygon points="0,0.75 5,0.25 5,3.15 0,3" fill='#D4D6D5' />
        <line x1='5' y1='0' x2='5' y2='8' stroke='#020202' strokeWidth='0.45' />
    </g>;
}

interface AxisProps {
    maxMiles: number;
    checkpoint: number;
    goal: number;
}

function Axis({ maxMiles, checkpoint, goal }: AxisProps): JSX.Element {
    const ticks = calcTicks(maxMiles);
    const minorTicks = calcMinorTicks(ticks);
    const checkpointLoc = (checkpoint / maxMiles) * 100;
    const goalLoc = (goal / maxMiles) * 100;
    return <>
        <line x1='-1' y1='10' x2='101' y2='10' stroke={colors.text} strokeWidth='0.5' />
        {ticks.map((arr, i) =>
            <line
                key={i}
                x1={arr[0]}
                y1='10'
                x2={arr[0]}
                y2='8.5'
                stroke={colors.text}
                strokeWidth='0.5'
            />,
        )}
        {ticks.map((arr, i) =>
            <text
                key={i}
                x={arr[0]}
                y='13'
                fill={colors.text}
                fontSize='3'
                textAnchor='middle'
                stroke='none'
            >
                {arr[1]}
            </text>,
        )}
        {minorTicks.map((pos, i) =>
            <line
                key={i}
                x1={pos}
                y1='10'
                x2={pos}
                y2='9'
                stroke={colors.text}
                strokeWidth='0.4'
            />,
        )}
        {checkpointLoc > 0 && <Banner loc={checkpointLoc} />}
        {goalLoc > 0 && <use xlinkHref='#flag' x={goalLoc - 3} y='-4' width='8' />}
    </>;
}

interface IRunnersProps {
    memberMiles: number[];
    miles: number;
    goal: number;
    checkpoint: number;
}

function RunnersFn({ memberMiles, miles, goal, checkpoint }: IRunnersProps): JSX.Element {
    const maxMiles = Math.max(goal, Math.max(...memberMiles));
    const stackedMiles = stackMiles(memberMiles, miles, maxMiles / 25.0);
    return <svg viewBox='-3 0 112 16'>
        <symbol id='flag' clipRule="evenodd" fillRule="evenodd" strokeLinejoin="round" strokeMiterlimit="2" viewBox="0 0 100 100">
            <path d="m86.406 31.579c.994 4.403 1.988 8.806 2.982 13.209-3.656 1.622-7.482 2.611-11.401 3.352-.955-4.182-1.925-8.36-2.881-12.542 3.823-1.197 7.856-1.816 11.3-4.019z" />
            <path d="m69.632 11.148c3.727-1.55 7.614-2.86 11.013-5.088 1.031 4.568 2.062 9.137 3.094 13.705-3.409 2.304-7.345 3.074-11.274 3.979-.947-4.198-1.886-8.398-2.833-12.596z" />
            <path d="m57.94 24.389c4.764.526 9.836.435 14.525-.645.881 3.951 1.76 7.903 2.641 11.854-4.573 1.433-9.65 1.521-14.378 1.06-.916-4.093-1.872-8.176-2.788-12.269z" />
            <path d="m63.642 49.214c-5.263-.104-10.543-.521-15.806-.152-1.039-4.641-2.125-9.272-3.165-13.914 5.388-.048 10.713.989 16.057 1.51.935 4.194 1.979 8.363 2.914 12.556z" />
            <path d="m39.006 10.64c5.453.334 10.705 2.05 16.146 2.483.909 3.76 1.879 7.505 2.788 11.266-5.38-.594-10.741-1.704-16.175-1.694-.919-4.018-1.839-8.036-2.759-12.055z" />
            <path d="m28.674 26.132c4.149-1.941 8.461-3.428 13.091-3.437.94 4.158 1.966 8.295 2.906 12.453-4.633.042-8.967 1.528-13.15 3.406-.935-4.144-1.912-8.279-2.847-12.422z" />
            <path d="m34.607 52.2c-4.128 1.502-8.257 3.277-11.991 5.61-.992-4.391-1.954-8.789-2.946-13.181 3.938-2.049 7.798-4.255 11.851-6.075 1.028 4.549 2.059 9.097 3.086 13.646z" />
            <path d="m16.895 32.47c-1.034-4.577-2.067-9.154-3.1-13.732 3.881-2.015 7.892-3.804 12.002-5.301.956 4.232 1.921 8.462 2.877 12.695-4.043 1.891-7.867 4.2-11.779 6.338z" />
            <path d="m6.042 21.228c-.363-1.609.648-3.211 2.258-3.574 1.609-.363 3.211.648 3.574 2.258l15.059 66.699c.363 1.609-.648 3.211-2.258 3.574-1.609.364-3.211-.648-3.574-2.257z" />
        </symbol>
        <symbol id='runner' viewBox='0 0 45.11 52.5'>
            <path
                d='M30.95 0a4.5 4.5 0 0 1 1 .19 7.39 7.39 0 0 1 1.61.59 6.19 6.19 0 0 1 1.94 1.63 5.23 5.23 0 0 1 1.05 1.82 6.18 6.18 0 0 1 .17 2.32 4.84 4.84 0 0 1-.25 1.53 4.35 4.35 0 0 1-.71 1.17 6.7 6.7 0 0 1-1.53 1.5 5.39 5.39 0 0 1-4.91.81 6.37 6.37 0 0 1-2.47-1.4A5.61 5.61 0 0 1 25 7.2a5.34 5.34 0 0 1 .12-2.68 6.12 6.12 0 0 1 2.07-3A7 7 0 0 1 29.35.27a4.09 4.09 0 0 1 1.6-.27zM25.83 11a18 18 0 0 1 4.74 1.7 10.26 10.26 0 0 1 .94 1.23 15.47 15.47 0 0 1 1.17 2.34c.71 1.6.73 1.68 1.05 2.28 0 0 .23.42.76 1.35L44 20a1.57 1.57 0 0 1 .94.7 1.59 1.59 0 0 1 .18 1.05 1.48 1.48 0 0 1-.23.64 1.71 1.71 0 0 1-.64.53 5.57 5.57 0 0 1-1.05.47c-1.59.47-2.38.7-2.69.76-1.94.37-1.88.06-3.92.41a21.58 21.58 0 0 1-2.75.41 4.17 4.17 0 0 1-1.87-.12 3.42 3.42 0 0 1-1.86-2 .52.52 0 0 0-.13-.33c-.64-.19-3.4 7.14-3.4 7.14.72.82 1.42 1.34 1.82 1.73 1.35 1.1 1.89 1.35 3.16 2.46a13.5 13.5 0 0 1 1.58 1.52 11.78 11.78 0 0 1 1.7 2.69c1.67 3.43 1.59 3.47 2.22 4.68 1.17 2.23 2.18 3.49 2.75 5.39a7.77 7.77 0 0 1 .23 2.17 2.44 2.44 0 0 1-.41 1.58 1.53 1.53 0 0 1-1.76.53 2 2 0 0 1-.87-.76c-1-1.47-2.44-3.73-3.16-4.8-.41-.6-.77-1.09-1.05-1.46-.73-1-1.14-1.43-1.87-2.34-1.11-1.38-1.2-1.6-1.76-2.28-1.57-1.91-4.44-4.49-5.62-3.81a2.89 2.89 0 0 0-.59.94 21.6 21.6 0 0 0-1 2.46 12.93 12.93 0 0 1-.64 1.52 8.53 8.53 0 0 1-1.58 1.87A6.17 6.17 0 0 1 18.22 45a4.39 4.39 0 0 1-1.81.47 11.24 11.24 0 0 1-3.1-.76c-.78-.18-1.94-.47-3.34-.88-3.5-1-3-1.2-5.09-1.7a10.69 10.69 0 0 1-3.51-1.29 2.81 2.81 0 0 1-1.13-1.2 2.38 2.38 0 0 1-.18-1.58 2 2 0 0 1 1-1.35 1.68 1.68 0 0 1 1-.06 13.26 13.26 0 0 1 2.75.64 18 18 0 0 0 6.44 1.05 12.65 12.65 0 0 0 2.87-.12 3 3 0 0 0 1.23-.35c.33-.22.7-.64 1.11-3a19.23 19.23 0 0 0 .35-2.93 24.22 24.22 0 0 0-.12-2.94c-.07-1.46.37-2.36 1.93-5.74C21 18 21.71 16.43 21.2 16c-.11-.1-.2-.07-1.76.12-2.05.24-2.06.21-2.4.29a7.82 7.82 0 0 0-2.75 1.41c-.34.23-1.37 1.06-3.4 2.69a8.89 8.89 0 0 1-1.76 1 1.66 1.66 0 0 1-1.05.23 1.36 1.36 0 0 1-1-.88 1.79 1.79 0 0 1 .29-1.41 8.09 8.09 0 0 1 1.46-2c1.3-1.31 1.95-2 2.87-2.75a32.2 32.2 0 0 1 3-2.34 12.77 12.77 0 0 1 1.87-1.17c.75-.33 1.66-.56 4.68-.47a26.44 26.44 0 0 1 4.58.28z'
                stroke='white'
                strokeWidth='1'
            />
        </symbol>
        <symbol id='runner1' viewBox='0 0 15 15'>
            <use xlinkHref='#runner' x='3' y='0' width='8.5' />
        </symbol>
        <symbol id='runner2' viewBox='0 0 15 10'>
            <use xlinkHref='#runner' x='2.2' y='-2' width='8' />
            <use xlinkHref='#runner' x='3' y='0' width='8.5' />
        </symbol>
        <symbol id='runner3' viewBox='0 0 15 10'>
            <use xlinkHref='#runner' x='1.5' y='-2.5' width='7.5' />
            <use xlinkHref='#runner' x='6.6' y='-1.7' width='7.2' />
            <use xlinkHref='#runner' x='3' y='0' width='8.5' />
        </symbol>
        <symbol id='runner4' viewBox='0 0 15 10'>
            <use xlinkHref='#runner' x='6.1' y='-4' width='6.8' />
            <use xlinkHref='#runner' x='1.5' y='-2.5' width='7' />
            <use xlinkHref='#runner' x='6.8' y='-1' width='7.5' />
            <use xlinkHref='#runner' x='3' y='0' width='8.5' />
        </symbol>
        <Axis maxMiles={maxMiles} checkpoint={checkpoint} goal={goal} />
        {stackedMiles.map(({ avg, count, player }, i) =>
            <React.Fragment key={i}>
                <use
                    xlinkHref={
                        count === 1 ? '#runner1' :
                            count === 2 ? '#runner2' :
                                count === 3 ? '#runner3' :
                                    '#runner4'
                    }
                    x={miles2pos(avg, maxMiles)}
                    y='-3'
                    width='8.5'
                    fill={colors.runner}
                />
                {player && (
                    <use
                        xlinkHref='#runner1'
                        x={miles2pos(avg, maxMiles)}
                        y='-3'
                        width='8.5'
                        fill={colors.text}
                    />
                )}
            </React.Fragment>,
        )}
    </svg>;
}

export const Runners = React.memo(RunnersFn);

interface IBikerProps {
    pathGroupMiles: number[];
    miles: number;
    goal: number;
    checkpoint: number;
}

function BikersFn({ pathGroupMiles, miles, goal, checkpoint }: IBikerProps): JSX.Element {
    const maxMiles = Math.max(goal, Math.max(...pathGroupMiles));
    const stackedMiles = stackMiles(pathGroupMiles, miles, maxMiles / 25.0);
    return <svg viewBox='-3 0 112 16'>
        <symbol id='biker' viewBox='0 0 100 100'>
            <path
                stroke='white'
                strokeWidth='1'
                d='M81.3,57.4c-0.3,0-0.6,0-0.9,0l-1.1-5.9l5-1.6c0.6-0.2,1-0.8,0.8-1.4l-1-5c-0.1-0.4-0.3-0.7-0.6-0.8
    c-0.3-0.2-0.7-0.2-1-0.1L78.1,44c-0.8-0.2-1.5-0.2-1.5-0.2s-5.8-3.6-6.3-4.3c-0.5-0.8-0.7-2.6-0.9-5c-0.1-1.7-1.4-3.4-1.2-5.1
    c0.1-0.8,0.3-1.3,0.6-1.6c0.6,0.2,1.1,0.3,1.6,0.5c1.3,0.5,1.7-0.6,1.8-1c0.2-0.8,1.2-1.7,1.2-2c0-0.3-0.1-0.6-0.2-1.3
    c-0.1-0.5,0.1-1.3,0.6-2.5c0.6-1.4-2.5-4.2-6.1-3.5c-1.3,0.3-2.5,0.9-3.3,2.6c-0.7,1.7,0,4.3,1.3,5.5c-0.3,1.8-3.1,1.9-4.4,4.6
    c-1.3,2.8-4.8,11.4-5.4,15.4c-0.1,0.5,0.5,0.7,1.1,1.1h-0.2c-0.7,0-1.2,0.6-1.2,1.2s0.6,1.2,1.2,1.2h0.3c0,0,0,0,0,0l1,6l-2.7,0.9
    l-1.6-9c0.1-0.1,0.3-0.3,0.4-0.4c0.5-0.8,0.3-2.6-0.6-3c-0.9-0.4-1.8-0.3-1.8-0.3s-5.8-3.6-6.3-4.3c-0.5-0.8-0.7-2.6-0.9-5
    c-0.1-1.7-1.4-3.4-1.2-5.1c0.1-0.8,0.3-1.3,0.6-1.6c0.6,0.2,1.1,0.3,1.6,0.5c1.3,0.5,1.7-0.6,1.8-1c0.2-0.8,1.2-1.7,1.2-2
    c0-0.3-0.1-0.6-0.2-1.3c-0.1-0.5,0.1-1.3,0.6-2.5c0.6-1.4-2.5-4.2-6.1-3.5c-1.3,0.3-2.5,0.9-3.3,2.6c-0.7,1.7,0,4.3,1.3,5.5
    c-0.3,1.8-3.1,1.9-4.4,4.6c-1.3,2.8-4.8,11.4-5.4,15.4c-0.1,0.5,0.5,0.7,1.1,1.1h-0.8c-0.7,0-1.2,0.6-1.2,1.2s0.6,1.2,1.2,1.2h0.6
    l0.4,2.2H11.9c-0.7,0-1.2,0.6-1.2,1.2c0,0.7,0.6,1.2,1.2,1.2h6l-0.1,3c-6.4,0.5-11.4,5.8-11.4,12.3c0,6.8,5.5,12.3,12.3,12.3
    C25.5,82,31,76.5,31,69.7c0-0.9-0.1-1.7-0.3-2.6l3.4-1.1l0.5,3c-0.8,1.7-1.5,3.2-1.5,3.5c0,0.7,0.7,1.2,0.7,1.2s-1.3,1-1.2,1.9
    c0,0.3,0.8,1,1.6,1.5c-0.1,0.2-0.1,0.3-0.1,0.5c0,0.7,0.6,1.2,1.2,1.2h5.2c0.7,0,1.2-0.6,1.2-1.2s-0.6-1.2-1.2-1.2h-0.1
    c-0.9-0.4-2.1-0.4-2.5-0.7c-0.7-0.5-0.9-0.8-0.9-0.8s0.9,0,1.2-0.4h20c-0.2,0.3-0.4,0.7-0.4,1.1c0,0.3,0.8,1,1.6,1.5
    c-0.1,0.2-0.1,0.3-0.1,0.5c0,0.7,0.6,1.2,1.2,1.2h5.2c0.7,0,1.2-0.6,1.2-1.2s-0.6-1.2-1.2-1.2h-0.1C64.6,76,63.4,76,63,75.7
    c-0.7-0.5-0.9-0.8-0.9-0.8s1.1,0,1.2-0.4c0.1-0.4,1.8-5,2.2-6.1c0.3-0.9,2.6-8.5,2.6-8.5s0.3-0.4,2-2c1.3-1.2,3.5-3.1,4.5-4.8
    l2.3-0.8l1,5.6c-5.2,1.5-9,6.2-9,11.9C69,76.5,74.5,82,81.3,82c6.8,0,12.3-5.5,12.3-12.3S88.1,57.4,81.3,57.4z M69.1,44.7
    c0.8,0.5,3.5,1.9,4.5,2.5c0.5,0.3,1.3-0.1,1.9-0.1c0.6,0.1,0.7,0.6,1.2,0.8c0.9,0.4,1.9,0.1,2.4-0.7c0.1-0.2,0.2-0.6,0.2-0.9
    l2.7-0.9l0.5,2.5l-5,1.6c0,0,0,0,0,0l-3.1,1c-2-2.1-8.1-5.5-8.1-5.5l0.9-1.8C67.3,43.1,68.4,44.2,69.1,44.7z M67.7,52.6l-1.7,0.5
    l-0.7-1.9L67.7,52.6z M59.9,52.6l1.2,2.2L60.3,55L59.9,52.6z M60.1,68.3c-0.7,1.4-1.4,2.9-1.6,3.7H39.1c0.5-1.5,1.1-3.1,1.4-3.7
    c0.2-0.5,0.9-2.7,1.5-4.7l16.4-5.3L60.1,68.3z M44.1,44.7c0.8,0.5,3.5,1.9,4.5,2.5c0.5,0.3,1.3-0.1,1.9-0.1c0.3,0,0.4,0.1,0.6,0.3
    l1.8,10l-10.1,3.2c0.1-0.5,0.2-0.8,0.2-0.8s0.3-0.4,2-2c1.7-1.6,5.1-4.5,4.9-6.4c-0.1-1.6-8.7-6.5-8.7-6.5l0.9-1.8
    C42.2,43.1,43.3,44.2,44.1,44.7z M43.7,53.2l-2,2l-1.4-3.9L43.7,53.2z M37.9,58.3c0,0-1.3,1.1-2.1,2l-1.5-8.7c0,0.1,0.1,0.1,0.1,0.1
    L37.9,58.3z M20.2,60c3.3,0.5,6,2.6,7.4,5.5L20,68L20.2,60z M28.5,69.7c0,5.4-4.4,9.8-9.8,9.8c-5.4,0-9.8-4.4-9.8-9.8
    c0-5.1,3.9-9.3,8.9-9.8l-0.3,9.8c0,0.4,0.2,0.8,0.5,1c0.2,0.2,0.5,0.3,0.7,0.3c0.1,0,0.3,0,0.4-0.1l9.3-3
    C28.5,68.5,28.5,69.1,28.5,69.7z M30,64.7c-1.7-3.9-5.3-6.7-9.7-7.3l0.1-3H32c0.1,0,0.2,0,0.2,0l1.6,9.1L30,64.7z M63,58.3
    c0,0-1,0.8-1.8,1.7l-0.4-2.5l1.5-0.5L63,58.3z M81.3,79.5c-5.4,0-9.8-4.4-9.8-9.8c0-4.4,2.9-8.1,6.9-9.4l1.7,9.6
    c0.1,0.6,0.6,1,1.2,1c0.1,0,0.1,0,0.2,0c0.7-0.1,1.1-0.8,1-1.4l-1.7-9.6c0.2,0,0.3,0,0.5,0c5.4,0,9.8,4.4,9.8,9.8
    C91.1,75.1,86.7,79.5,81.3,79.5z'
            />
        </symbol>
        <symbol id='biker1' viewBox='0 0 15 10'>
            <use xlinkHref='#biker' x='2' y='0' width='11' />
        </symbol>
        <symbol id='biker2' viewBox='0 0 15 10'>
            <use xlinkHref='#biker' x='1.2' y='-2' width='10' />
            <use xlinkHref='#biker' x='2' y='0' width='11' />
        </symbol>
        <symbol id='biker3' viewBox='0 0 15 10'>
            <use xlinkHref='#biker' x='0.8' y='-2.5' width='9' />
            <use xlinkHref='#biker' x='5.6' y='-1.7' width='9.5' />
            <use xlinkHref='#biker' x='2' y='0' width='11' />
        </symbol>
        <symbol id='biker4' viewBox='0 0 15 10'>
            <use xlinkHref='#biker' x='5.1' y='-4' width='7.8' />
            <use xlinkHref='#biker' x='0.8' y='-2.5' width='8.7' />
            <use xlinkHref='#biker' x='5.7' y='-1' width='9.5' />
            <use xlinkHref='#biker' x='2' y='0' width='11' />
        </symbol>
        <Axis maxMiles={maxMiles} checkpoint={checkpoint} goal={goal} />
        {stackedMiles.map(({ avg, count, player }, i) =>
            <React.Fragment key={i}>
                <use
                    key={i}
                    xlinkHref={
                        count === 1 ? '#biker1' :
                            count === 2 ? '#biker2' :
                                count === 3 ? '#biker3' :
                                    '#biker4'
                    }
                    x={miles2pos(avg, maxMiles)}
                    y='-3'
                    width='12'
                    fill={colors.runner}
                />
                {player && (
                    <use
                        xlinkHref='#biker1'
                        x={miles2pos(avg, maxMiles)}
                        y='-3'
                        width='12'
                        fill={colors.text}
                        key={'player'}
                    />
                )}
            </React.Fragment>,
        )}
    </svg>;
}

export const Bikers = React.memo(BikersFn);
